import { useState } from 'react';
import type { TraMemberAdmin, TraCertLevel } from '@/lib/api/traAdminClient';
import ModuleLayout from '../../components/admin/ModuleLayout';
import DataTable, { type DataTableColumn } from '../../components/ui/table/DataTable';
import { RowActionsMenu } from '../../components/admin/RowActionsMenu';
import { useServerTable } from '../../hooks/useServerTable';
import { traNavTabs } from '@/components/tra/traNavTabs';
import { PencilIcon } from '../../icons';

const LEVEL_OPTIONS: { label: string; value: string }[] = [
  { label: 'Todos los niveles', value: '' },
  { label: 'L1', value: 'L1' },
  { label: 'L2', value: 'L2' },
  { label: 'L3', value: 'L3' },
  { label: 'TMP', value: 'TMP' },
];

export default function MembersListPage() {
  const [search, setSearch] = useState('');
  const [level, setLevel] = useState<TraCertLevel | ''>('');

  const {
    rows: members,
    total,
    page,
    setPage,
    pageSize,
    setPageSize,
    sort,
    setSort,
    loading,
    reload,
  } = useServerTable<TraMemberAdmin>({
    endpoint: '/tra/members/admin',
    defaultPageSize: 20,
    defaultSort: { column: 'createdAt', dir: 'desc' },
    params: { search, level: level || undefined },
    extractRows: (data) => data.items ?? [],
    extractTotal: (data) => data.total ?? 0,
  });

  const columns: DataTableColumn<TraMemberAdmin>[] = [
    {
      key: 'fullName',
      label: 'Nombre',
      sortable: true,
      render: (member) => (
        <>
          <p className="font-medium text-gray-900 dark:text-white">{member.fullName}</p>
          {member.traNumber ? <p className="text-xs text-gray-500">TRA #{member.traNumber}</p> : null}
        </>
      ),
    },
    {
      key: 'level',
      label: 'Nivel',
      sortable: true,
      render: (member) => (
        <span className="inline-flex rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-semibold text-brand-600 dark:bg-brand-500/10 dark:text-brand-300">
          {member.level}
        </span>
      ),
    },
    {
      key: 'institutionName',
      label: 'Institución',
      render: (member) => <span className="text-gray-600 dark:text-gray-400">{member.institutionName ?? '—'}</span>,
    },
    {
      key: 'city',
      label: 'Ciudad',
      render: (member) => <span className="text-gray-600 dark:text-gray-400">{member.city ?? '—'}</span>,
    },
    {
      key: 'edition',
      label: 'Edición',
      render: (member) => <span className="text-xs text-gray-500">{member.edition?.name ?? '—'}</span>,
    },
    {
      key: 'certifiedAt',
      label: 'Certificado',
      sortable: true,
      render: (member) => (
        <span className="text-xs text-gray-500">
          {member.certifiedAt ? new Date(member.certifiedAt).toLocaleDateString('es-CO') : '—'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Acción',
      align: 'right',
      render: (member) => (
        <div className="flex justify-end">
          <RowActionsMenu
            actions={[
              {
                label: 'Editar certificación',
                icon: <PencilIcon />,
                to: `/tra/members/${member.id}`,
                requires: 'tripoli:members:edit',
              },
            ]}
          />
        </div>
      ),
    },
  ];

  return (
    <ModuleLayout
      breadcrumbs={[{ label: 'SateLab', to: '/' }, 'TRA', 'Miembros certificados']}
      title="Miembros certificados"
      subtitle="Directorio de todos los certificados Tripoli Colombia, a través de todas las ediciones"
      tabs={traNavTabs('members')}
      metrics={[{ label: 'Total certificados', value: String(total) }]}
      toolbar={{
        search: { value: search, onChange: setSearch, placeholder: 'Buscar por nombre...' },
        filters: [
          {
            type: 'select',
            key: 'level',
            label: 'Nivel',
            value: level,
            onChange: (v) => setLevel(v as TraCertLevel | ''),
            options: LEVEL_OPTIONS,
          },
        ],
        onRefresh: reload,
        refreshing: loading,
      }}
    >
      <DataTable
        columns={columns}
        rows={members}
        rowKey={(member) => member.id}
        loading={loading}
        emptyMessage="No hay miembros certificados todavía."
        sort={sort}
        onSortChange={setSort}
        page={page}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        total={total}
        onPageChange={setPage}
      />
    </ModuleLayout>
  );
}
